import { Schema } from "effect"
import { MonitorQuiet, MonitorWhen } from "./agent-page"
import { CommandRejection, CommandResult } from "./commands/index"
import { AgentKind, Evidence, Health, Risk } from "./common"
import { WorkReply, WorkVerification } from "./coordination"
import { DocMeta } from "./documents"
import { BriefSections } from "./domain/brief-sections"
import { ArtifactKind, Blocker, CloseResult } from "./domain/item"
import { KpiEvidence } from "./domain/plan"
import { CARD_ASK_MAX, CardRecommendation, PlanQuestion } from "./domain/proposal"
import { WorkerSettings } from "./domain/workspace"
import {
  ArtifactId,
  BranchName,
  ChangesetId,
  CommentId,
  FrameSeq,
  Handle,
  Millis,
  PosInt,
  ProjectId,
  ProposalId,
  RepoId,
  Sha256,
  Slug,
  WorkspaceId,
} from "./ids"
import { DateBound, KnowledgeToolInputs } from "./knowledge-search"
import { RoutingPolicy } from "./routing"
import { RunOutcome, Verdict } from "./run"
import { DEFAULT_ORIGIN, SkillDigest, SkillName, skillBody } from "./skills/index"

// #region graph-tools
/**
 * The graph tools of the integration wave (decision 0011, §6.9 of the wave spec). The Project DO serves them over
 * MCP (`POST /api/runs/:runId/mcp`, runner minds) and in process (`ProjectDO.callTool`, platform minds). Every call
 * becomes a CommandEnvelope and is re-checked by `decide`.
 */

/** A label an agent defines in a plan (`$offline-toggle`) and references elsewhere in the same plan. */
export const PlanLabel = Schema.String.check(Schema.isPattern(/^\$[a-z0-9][a-z0-9-]{0,39}$/))
/**
 * Where an id goes, an agent may write a real id, a short ref (DIC-12; older items keep T-12, I-3, C-57), an @handle or
 * a $label. Resolved by the tool layer.
 */
export const ToolRef = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))

const ToolName = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128))

/**
 * Decision 0095: `retry_item`'s place for the next attempt. Runner and profile by name or id; `account` is a profile's
 * `subscriptionId` or `quotaAccountKey` (or its first characters) from list_runners. `mode` defaults to required.
 */
export const RetryRoute = Schema.Struct({
  runner: Schema.optionalKey(ToolName),
  profile: Schema.optionalKey(ToolName),
  cli: Schema.optionalKey(AgentKind),
  model: Schema.optionalKey(ToolName),
  account: Schema.optionalKey(ToolName),
  mode: Schema.optionalKey(Schema.Literals(["required", "preferred"])),
  reason: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(600))),
})
export const RetryAvoid = Schema.Struct({ runner: ToolName, profile: Schema.optionalKey(ToolName) })

/** close_item evidence: protocol Evidence, or sugar the tool layer resolves (`{run: "R-24"}`, `{artifact: "<id>"}`). */
export const CloseEvidence = Schema.Union([
  Evidence,
  Schema.Struct({ run: ToolRef }),
  Schema.Struct({ artifact: ToolRef }),
])

/** record_kpi evidence (decision 0045): KpiEvidence, or the same `{run}` / `{artifact}` sugar as close_item. */
export const RecordKpiEvidence = Schema.Union([
  KpiEvidence,
  Schema.Struct({ run: ToolRef }),
  Schema.Struct({ artifact: ToolRef }),
])

/** Decision 0097: one sample of a bulk `record_kpi`. */
export const RecordKpiSampleInput = Schema.Struct({
  kpi: Schema.optionalKey(ToolRef),
  value: Schema.Finite,
  at: Millis,
  provisional: Schema.optionalKey(Schema.Boolean),
  n: Schema.optionalKey(Schema.NullOr(Schema.Int)),
  evidence: Schema.optionalKey(Schema.Array(RecordKpiEvidence).check(Schema.isMaxLength(20))),
})

/** Wave O1 (decision 0061, decide first): an open question on a plan card, with Lea's recommended answer. */
export const PlanQuestionInput = PlanQuestion
export type PlanQuestionInput = typeof PlanQuestionInput.Type

/**
 * Decide first (decision 0061, wave spec §5.4), on `plan` and `propose_plan`: the questions only the person can answer,
 * each with Lea's recommendation, and the container (a ref, an id or a `$label` of a CreateItem in `changes`) whose
 * description receives the answers. They show only on a card; an applied `plan` drops them with a warning.
 */
/**
 * Decision 0144 (0136 §3): every card a person answers leads with the question and the proposer's recommendation.
 * `ask`: the question itself ("Start sync PR 1 now, before your library test?"). `recommendation`: the answer you
 * recommend (an option key, or approve / reject) and why, in one sentence.
 */
const CardShapeFields = {
  ask: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(CARD_ASK_MAX))),
  recommendation: Schema.optionalKey(CardRecommendation),
}

const DecideFirstFields = {
  questions: Schema.optionalKey(Schema.Array(PlanQuestionInput).check(Schema.isMaxLength(8))),
  container: Schema.optionalKey(ToolRef),
}

/** Wave C (decision 0071): the kinds `remember` writes (the schema keeps decision, playbook and journal for later). */
export const RememberKind = Schema.Literals(["rule", "preference", "fact", "learning"])
export type RememberKind = typeof RememberKind.Type

/**
 * A memory's fields as an agent or a person writes them (`remember`). One object root (tool schemas never have a
 * root-level anyOf): a new memory names `kind`, `title` and `text`; `replaces: "M-7"` writes a new revision of it;
 * `forget: "M-7"` alone forgets one. The tool layer checks which of the three a call is and refuses a mix.
 */
const rememberFields = {
  kind: Schema.optionalKey(RememberKind),
  /** One line, ≤ 160 characters (it is the index line). */
  title: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160))),
  text: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_000))),
  /** A preference: the person it is about (`@handle` or user id). */
  about: Schema.optionalKey(ToolRef),
  /** A new revision of this memory (M-7) instead of a new one. */
  replaces: Schema.optionalKey(ToolRef),
  /** Forget this memory (M-7); nothing else but `why`. */
  forget: Schema.optionalKey(ToolRef),
  /** Forget: why, one line. */
  why: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(600))),
  /** Refs that back it (R-9, DIC-12, O-14); a learning needs at least one. */
  evidence: Schema.optionalKey(Schema.Array(ToolRef).check(Schema.isMaxLength(10))),
  /** Where it came from: a record's ref, or a chat message as `message:<messageId>`. Checked by decide. */
  source: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300))),
  /**
   * Decision 0146: a new claim that replaces this one (M-7 or PM-3). The old claim is kept as history (superseded,
   * `validTo` = when the new one starts); a person's claim is superseded by a card to that person.
   */
  supersedes: Schema.optionalKey(ToolRef),
  /** Decision 0146: this memory (M-7 / PM-3) was checked against the record and still holds; nothing else. */
  confirm: Schema.optionalKey(ToolRef),
  /** Decision 0146: when the claim starts or stops being true in the world (YYYY-MM-DD or epoch ms). */
  validFrom: Schema.optionalKey(DateBound),
  validTo: Schema.optionalKey(DateBound),
  /**
   * Decision 0146: add it although similar claims exist (`remember` shows them first and writes nothing until you
   * say: `supersedes` one, `replaces` one, or `add: true` because it is a different claim).
   */
  add: Schema.optionalKey(Schema.Boolean),
}

/**
 * Wave C (decision 0071): `remember` (it replaces the never-built `propose_memory`). `scope`: the project (default)
 * or your area (a worker: its item's area). Routing (spec §4.3): in a turn a person started it applies as they asked;
 * otherwise your autonomy level decides; rules from agents are always a card; a worker's goes to its item's owner
 * agent.
 */
export const RememberInput = Schema.Struct({
  ...rememberFields,
  /**
   * `personal` (decision 0146): a private note of the person whose ask this turn serves (PM-n), never shown to anyone
   * else; for their own preferences and facts about themselves.
   */
  scope: Schema.optionalKey(Schema.Literals(["project", "area", "personal"])),
})
export type RememberInput = typeof RememberInput.Type

/**
 * Tool inputs (decision 0037). `plan`/`propose_plan` changes are GraphCommands as JSON with refs and labels in id
 * positions; the tool layer resolves them, fills defaults, then decodes each with GraphCommand (strictly; a change that
 * fails and carries a v1 tag is decoded with the v1 schema and upcast, with a warning). Evidence in report_outcome and
 * report_verdict: the tool layer fills an omitted `check.detail` with null before decoding (A19). Old argument names
 * (`task` for `item`, `depends_on.taskId`) are mapped by the tool layer before decoding (one release).
 */
export const GraphToolInputs = {
  read_context: Schema.Struct({}),
  read_graph: Schema.Struct({
    include: Schema.optionalKey(Schema.Array(Schema.Literals(["done", "cancelled", "archived"]))),
  }),
  plan: Schema.Struct({
    rationale: Schema.NonEmptyString,
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
    mode: Schema.optionalKey(Schema.Literals(["apply_allowed", "propose_all"])),
    ...DecideFirstFields,
  }),
  propose_plan: Schema.Struct({
    rationale: Schema.NonEmptyString,
    oneLine: Schema.String.check(Schema.isMaxLength(140)),
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
    risk: Schema.optionalKey(Risk),
    decideBy: Schema.optionalKey(Millis),
    /** Decision 0127: who approves, a member's @handle or user id. Default: the person who asked, else the admins. */
    to: Schema.optionalKey(ToolRef),
    ...DecideFirstFields,
    ...CardShapeFields,
  }),
  /**
   * Sugar for `plan` with one CreateItem: the CreateItem fields with refs in id positions (`parentId: "DIC-40"`,
   * `owner: "@ios-dev"`, `assignee: "worker" | "worker:<preset>" | "@person"`). `item` is an optional $label for the new
   * item (the answer maps it to the minted id and ref). Capture = `status: "triage"`. The tool layer builds the
   * change and decodes it with GraphCommand.
   */
  create_item: Schema.StructWithRest(
    Schema.Struct({
      item: Schema.optionalKey(PlanLabel),
      title: Schema.NonEmptyString,
      why: Schema.NonEmptyString,
    }),
    [Schema.Record(Schema.String, Schema.Unknown)],
  ),
  ask_human: Schema.Struct({
    prompt: Schema.NonEmptyString,
    options: Schema.Array(
      Schema.Struct({
        key: Slug,
        label: Schema.String,
        detail: Schema.optionalKey(Schema.String),
        /** Commands that run as the person who picks this option (OptionEffect JSON with refs, like `plan` changes). */
        effects: Schema.optionalKey(Schema.Array(Schema.Unknown).check(Schema.isMaxLength(10))),
        /** Releases the `blocks` gate (default: key "approve"); any other answer pauses the gated items. */
        releases: Schema.optionalKey(Schema.Boolean),
      }),
    ).check(Schema.isMaxLength(8)),
    allowFreeText: Schema.optionalKey(Schema.Boolean), // default true
    subject: Schema.optionalKey(ToolRef),
    blocks: Schema.optionalKey(Schema.Array(ToolRef)),
    decideBy: Schema.optionalKey(Millis),
    /**
     * Who answers: a member's @handle or user id (decision 0127: the person whose "Decides" on their people page
     * covers the topic). Default: the person responsible for the subject item, else the person who asked, else the
     * project's admins.
     */
    to: Schema.optionalKey(ToolRef),
    /** Wave B2 (decision 0057): why you ask, shown under the question. Optional so recorded inputs keep decoding. */
    why: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(600))),
    ...CardShapeFields,
  }),
  comment: Schema.Struct({
    target: ToolRef,
    body: Schema.NonEmptyString,
    threadId: Schema.optionalKey(CommentId),
    /** Decision 0049: records a decision on the item or goal it changed (what, why and where it came from). */
    decision: Schema.optionalKey(Schema.Boolean),
  }),
  /** An `update` comment with a health on a goal or an item (its health until the next update). */
  post_update: Schema.Struct({ target: ToolRef, health: Health, body: Schema.NonEmptyString }),
  /**
   * Decision 0098: a brief to a person (what happened, what's next, what they need to do, by ref). Decision 0130: `to`
   * is a member's @handle or user id (one brief per person; default the agent's keeper).
   */
  send_brief: Schema.Struct({
    /** Decision 0098: the markdown brief. Decision 0144: optional when `headline` (the sections) is given. */
    body: Schema.optionalKey(Schema.String),
    to: Schema.optionalKey(ToolRef),
    // decision 0144: the sections (see BriefSections); `headline` makes it a sectioned brief
    headline: Schema.optionalKey(BriefSections.fields.headline),
    asks: BriefSections.fields.asks,
    waiting: BriefSections.fields.waiting,
    didntWork: BriefSections.fields.didntWork,
    happened: BriefSections.fields.happened,
    next: BriefSections.fields.next,
    text: BriefSections.fields.text,
  }),
  /** `to`: the agent asked (`@handle`, any agent but yourself; Wave A); else one level up. */
  ask: Schema.Struct({ target: ToolRef, body: Schema.NonEmptyString, to: Schema.optionalKey(ToolRef) }),
  answer: Schema.Struct({ askId: CommentId, body: Schema.NonEmptyString }),
  /**
   * Decision 0142: a message in a thread (`T-n`). `to`: agents (`@gia`), people (`@jannik`), a person's external
   * participants (`@dot` is the asker's or the only one; `@jannik.dot`), live workers (`W-31`); omitted in a thread:
   * its other parties. `thread` continues one; `on` attaches a new one to a record (DIC-12, P-4, G-2).
   * `expectsReply`: an ask (a reply is expected); otherwise information the recipient reads in its next turn.
   */
  message: Schema.Struct({
    to: Schema.optionalKey(Schema.Array(ToolRef).check(Schema.isMaxLength(20))),
    body: Schema.NonEmptyString.check(Schema.isMaxLength(40_000)),
    thread: Schema.optionalKey(ToolRef),
    on: Schema.optionalKey(ToolRef),
    expectsReply: Schema.optionalKey(Schema.Boolean),
  }),
  /**
   * Decision 0142: a teammate agent's team chat in order (people's messages, its replies, messages between agents; tool
   * calls as one line). Newest `limit` by default; `before`/`after`/`around` a message id page through it.
   */
  read_chat: Schema.Struct({
    agent: ToolRef,
    before: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
    after: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
    around: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
    limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
  }),
  report_outcome: Schema.Struct({ outcome: RunOutcome }),
  report_blocker: Schema.Struct({ summary: Schema.String, blocker: Blocker }),
  report_verdict: Schema.Struct({ verdict: Verdict }),
  post_artifact: Schema.Struct({
    kind: ArtifactKind,
    title: Schema.String,
    /** Exactly one of uri | path | fromChat (the tool layer checks). */
    uri: Schema.optionalKey(Schema.String), // a link (https://…) or an r2://artifacts/<sha> the run uploaded
    /** Runner ≥ 0.6.0 only: a file in the run's working directory; the runner uploads it and sends `uri`. */
    path: Schema.optionalKey(Schema.String),
    /** Inline text, also available to platform agents without a filesystem. */
    content: Schema.optionalKey(Schema.String),
    /** Project knowledge: its canonical path and optional original. Agents retain authorship. */
    doc: Schema.optionalKey(DocMeta),
    /** Agents only: the sha256 of a file dropped into the chat (the `fromChat:` marker in the message). */
    fromChat: Schema.optionalKey(Sha256),
    contentType: Schema.optionalKey(Schema.String),
    preview: Schema.optionalKey(Schema.String),
    item: Schema.optionalKey(ToolRef),
    /** A new version of an artifact on the same item (its id). */
    supersedes: Schema.optionalKey(ToolRef),
    /** A file from outside the project (a tester's or customer's screenshot): marks it tainted. */
    untrusted: Schema.optionalKey(Schema.Boolean),
  }),
  review: Schema.Struct({
    item: ToolRef,
    verdict: Schema.Literals(["approve", "changes_requested"]),
    notes: Schema.String,
    findings: Schema.optionalKey(Schema.Array(Schema.String)),
  }),
  retry_item: Schema.Struct({
    item: ToolRef,
    note: Schema.optionalKey(Schema.String),
    /** Decision 0095: where the next attempt runs (names or ids from list_runners). */
    routing: Schema.optionalKey(RetryRoute),
    /** Decision 0095: places the next attempt never uses. */
    avoid: Schema.optionalKey(Schema.Array(RetryAvoid)),
  }),
  unblock_item: Schema.Struct({
    item: ToolRef,
    note: Schema.NonEmptyString,
    hold: Schema.optionalKey(Schema.Boolean),
  }),
  // ---- fix wave 2026-10-09 §4.6 (decision 0021) ----
  /** "P-3" or an id: the full card, including its answer. */
  read_proposal: Schema.Struct({ proposal: ToolRef }),
  /** "DIC-12" (or T-1, I-3, C-57) or an id: the item in full, with its runs, children, thread and proposals. */
  read_item: Schema.Struct({ item: ToolRef }),
  /** "R-9" or an id: the run's result and (optionally) the tail of its transcript, scrubbed of secrets. */
  read_run: Schema.Struct({
    run: ToolRef,
    transcript: Schema.optionalKey(
      Schema.Struct({
        /** Messages from the end; default 10. */
        last: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
        /** Paging: messages before this frameSeq (`nextBefore` of the previous page). */
        before: Schema.optionalKey(FrameSeq),
      }),
    ),
  }),
  /** The workspace's runners as this project's principal sees them: status, profiles, access modes, MCP names. */
  list_runners: Schema.Struct({}),
  stop_run: Schema.Struct({ run: ToolRef, reason: Schema.NonEmptyString }),
  withdraw_proposal: Schema.Struct({ proposal: ToolRef, note: Schema.optionalKey(Schema.String) }),
  /**
   * Decision 0093: the person answered one of their open cards in this chat; apply that answer for them. `quote`: their
   * words that answer it. `option` (a question's option key) and/or `text` for a question; `approve` / `reject` for a
   * plan, memory or monitor card.
   */
  resolve_card: Schema.Struct({
    card: ToolRef,
    quote: Schema.NonEmptyString.check(Schema.isMaxLength(500)),
    option: Schema.optionalKey(Schema.String),
    text: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4_000))),
    decision: Schema.optionalKey(Schema.Literals(["approve", "reject"])),
    /**
     * Decision 0142: the person said it in a teammate agent's team chat (`@bob`), not in yours: `message` is that chat
     * message's id (read_chat shows it) and `quote` its words.
     */
    chat: Schema.optionalKey(ToolRef),
    message: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
  }),
  /**
   * Decision 0102: decide a card addressed to you (your team's card, before a person sees it), within what you could
   * do yourself. A question: `option` and/or `text`. Any other card: `decision` approve, reject or send_back. `note`:
   * why (the proposer and the person read it).
   */
  decide_card: Schema.Struct({
    card: ToolRef,
    note: Schema.NonEmptyString.check(Schema.isMaxLength(2_000)),
    option: Schema.optionalKey(Schema.String),
    text: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4_000))),
    decision: Schema.optionalKey(Schema.Literals(["approve", "reject", "send_back"])),
  }),
  /** Decision 0102: pass a card addressed to you one level up, with your recommendation (what you'd choose and why). */
  escalate_card: Schema.Struct({
    card: ToolRef,
    note: Schema.NonEmptyString.check(Schema.isMaxLength(2_000)),
  }),
  // ---- lead control wave (decision 0032), renamed (decision 0037) ----
  close_item: Schema.Struct({
    item: ToolRef,
    /** `impossible`: AcceptImpossible when the item has a claim, else CloseItem{cancelled, impossible}. */
    as: Schema.Literals(["done", "cancelled", "impossible"]),
    note: Schema.NonEmptyString,
    /** Required for `done` when the item has command or artifact checks (checked by decide); ignored otherwise. */
    evidence: Schema.optionalKey(Schema.Array(CloseEvidence).check(Schema.isMaxLength(20))),
    /** `cancelled`: a duplicate of this item (resolution duplicate). */
    duplicateOf: Schema.optionalKey(ToolRef),
    /** `cancelled`: replaced by this item (resolution superseded). */
    supersededBy: Schema.optionalKey(ToolRef),
    /**
     * Decision 0101, `done`: what it delivered: `verified` (default), `report_delivered` (a report or finding is the
     * deliverable) or `no_data` (the work found nothing to deliver, and says so).
     */
    result: Schema.optionalKey(CloseResult),
    /**
     * Decision 0101, `cancelled`: it can't be done here and nobody here can grant what it needs; name the outside blocker
     * ("App Store Connect write key"). Resolution `blocked_external`.
     */
    blockedBy: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300))),
  }),
  pause_item: Schema.Struct({
    item: ToolRef,
    paused: Schema.Boolean,
    note: Schema.optionalKey(Schema.String),
  }),
  revise_proposal: Schema.Struct({
    proposal: ToolRef,
    rationale: Schema.NonEmptyString,
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
  }),
  // ---- decision 0045 ----
  /**
   * One KPI sample with evidence (≥ 1): `at` is the period the value describes (not when it was fetched). A newer sample
   * for the same period replaces the older one; the history keeps both.
   */
  record_kpi: Schema.Struct({
    kpi: Schema.optionalKey(ToolRef),
    value: Schema.optionalKey(Schema.Finite),
    at: Schema.optionalKey(Millis),
    provisional: Schema.optionalKey(Schema.Boolean),
    n: Schema.optionalKey(Schema.NullOr(Schema.Int)),
    /** Required for agents and workers: on the call (it covers every sample) or on each sample. */
    evidence: Schema.optionalKey(
      Schema.Array(RecordKpiEvidence).check(Schema.isMinLength(1), Schema.isMaxLength(20)),
    ),
    /**
     * Decision 0097: many numbers in one call (a backfill, or several KPIs from one fetch). Each takes `kpi` and
     * `evidence` from the call unless it names its own; all apply or none.
     */
    samples: Schema.optionalKey(
      Schema.Array(RecordKpiSampleInput).check(Schema.isMinLength(1), Schema.isMaxLength(500)),
    ),
  }),
  // ---- wave K (decision 0065): search, read, look_back ----
  ...KnowledgeToolInputs,
  // ---- wave C (decision 0071): memory and monitors ----
  remember: RememberInput,
  /** Decision 0146 (L5): what tending looks at. `area`: one area's memory (default: yours, the lead's: all). */
  memory_review: Schema.Struct({
    area: Schema.optionalKey(ToolRef),
    limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
  }),
  set_monitor: Schema.Struct({
    monitor: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1))),
    instruction: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(600)),
    when: MonitorWhen,
    quietHours: Schema.optionalKey(Schema.NullOr(MonitorQuiet)),
  }),
  steer_monitor: Schema.Struct({
    monitor: Schema.String.check(Schema.isMinLength(1)),
    action: Schema.Literals(["pause", "resume", "remove"]),
  }),
  list_projects: Schema.Struct({}),
  request_work: Schema.Struct({
    project: ToolRef,
    agent: Schema.optionalKey(ToolRef),
    request: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1))),
    revision: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThan(0))),
    title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
    body: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(40_000)),
    item: Schema.optionalKey(ToolRef),
    knowledge: Schema.optionalKey(Schema.Array(ToolRef).check(Schema.isMaxLength(20))),
    intentKey: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
  }),
  verify_work: Schema.Struct({
    request: ToolRef,
    reply: ToolRef,
    artifact: ToolRef,
    headSha: WorkVerification.fields.headSha,
    verdict: WorkVerification.fields.verdict,
    note: WorkVerification.fields.note,
  }),
  reply_work: Schema.Struct({
    request: ToolRef,
    revision: Schema.Int.check(Schema.isGreaterThan(0)),
    outcome: WorkReply.fields.outcome,
    summary: WorkReply.fields.summary,
    links: Schema.optionalKey(WorkReply.fields.links),
    artifacts: Schema.optionalKey(Schema.Array(ToolRef).check(Schema.isMaxLength(20))),
  }),
  cancel_work: Schema.Struct({
    request: ToolRef,
    revision: Schema.Int.check(Schema.isGreaterThan(0)),
    note: Schema.NonEmptyString,
  }),
  link_work: Schema.Struct({
    request: ToolRef,
    revision: Schema.Int.check(Schema.isGreaterThan(0)),
    item: ToolRef,
  }),
  read_work: Schema.Struct({
    request: ToolRef,
    artifact: Schema.optionalKey(ToolRef),
    offset: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  }),
  /** Decision 0150: save an artifact's file into the run's working directory (served by a runner with artifact_fetch). */
  fetch_artifact: Schema.Struct({ ref: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)) }),
  propose_monitor: Schema.Struct({
    /** What she should do when it fires, one line ("Check the App Store reviews and tell me what changed"). */
    instruction: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
    when: MonitorWhen,
    /** Absent: 20:00-08:00 for every-N-hours, none otherwise. null: no quiet hours. In the project's zone. */
    quietHours: Schema.optionalKey(Schema.NullOr(MonitorQuiet)),
    /** Why, shown on the card. */
    why: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(600))),
  }),
} as const
export type GraphToolName = keyof typeof GraphToolInputs
export const GraphToolNames = Object.keys(GraphToolInputs) as ReadonlyArray<GraphToolName>

/** Every graph tool answers with this JSON (MCP: structuredContent + the same JSON as one text block). */
export const GraphToolOutput = Schema.Struct({
  ok: Schema.Boolean,
  /** Commands. */
  result: Schema.optionalKey(CommandResult),
  /** ok = false (MCP isError = true). */
  rejection: Schema.optionalKey(CommandRejection),
  /** How to fix and retry: one line. */
  hint: Schema.optionalKey(Schema.String),
  /** The raw schema error next to the one-line hint (≤ 800 chars). */
  detail: Schema.optionalKey(Schema.String),
  /** Things worth knowing that did not stop the call (plan: no repo, runner offline, profile not ready). */
  warnings: Schema.optionalKey(Schema.Array(Schema.String)),
  /** Set whenever a proposal was opened (chat cards key on it). */
  proposalId: Schema.optionalKey(ProposalId),
  /**
   * Wave A §5: an applied `plan` / `create_item` changeset that can be undone right after it applied (core `undoable`);
   * the chat offers Undo (`UndoChangeset`) on it. Never on closes, cancels or `close_item`.
   */
  changesetId: Schema.optionalKey(ChangesetId),
  /** $label → minted id. */
  ids: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
  /** $label → short ref, when assigned. */
  refs: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
  /** Read tools. */
  data: Schema.optionalKey(Schema.Json),
  /**
   * Decision 0142: messages for the calling person's client (their dot, their coding session) that it had not picked
   * up, delivered with this answer (and marked read). Person tools only.
   */
  messages: Schema.optionalKey(Schema.Array(Schema.Json)),
})
export type GraphToolOutput = typeof GraphToolOutput.Type
// #endregion graph-tools

// #region person-tools
/**
 * The external MCP server (decision 0002, decision 0046): `https://app.minga.run/mcp`, OAuth only, acting as the
 * signed-in person for their own Claude Code or Codex. It never acts as an agent; approvals of plans and outward
 * actions stay in the Minga app. Each input is the lead's tool input (`GraphToolInputs`) plus `project`: a project id,
 * slug or name, or a git remote of a repo registered to it. Execution: `ProjectDO.personTool` (apps/cloud
 * project/tools/person.ts); `list_projects`, `needs_me` and `ask_lead` run in the Worker.
 *
 * Text limits (§4.3, apps/cloud src/mcp/limits.ts): any body ≤ 40,000 characters, a note ≤ 4,000, a rationale ≤ 2,000;
 * titles, why and bodies inside `plan` changes are checked by the person path.
 */
const P = { project: ToolRef }
const { project: _workDestination, ...personWorkFields } = GraphToolInputs.request_work.fields
const { as: _closeAs, ...closeItemFields } = GraphToolInputs.close_item.fields

/**
 * Wave O1 (decision 0060, wave spec §3.6): the person's key for one write. The explicit key wins over Claude Code's
 * tool-use id, so a retry from any client (Codex too) is deduplicated for 7 days. A key is a promise about one body:
 * the same key with a different body is refused (`Conflict`).
 */
export const IdempotencyKey = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,128}$/))

/** A git remote as a person writes it (`git@github.com:org/repo.git`, `github.com/org/repo`); the cloud normalizes it. */
const GitRemote = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500))

/** An `https:` URL of at most 2,000 characters (G2 `attach`). */
export const HttpsUrl = Schema.String.check(
  Schema.isMaxLength(2_000),
  Schema.isPattern(/^https:\/\/[^\s/]+\S*$/),
)

export const PersonToolInputs = {
  list_projects: Schema.Struct({}),
  read_project: Schema.Struct({
    ...P,
    include: Schema.optionalKey(Schema.Array(Schema.Literals(["done", "cancelled", "archived"]))),
  }),
  read_item: Schema.Struct({ ...P, ...GraphToolInputs.read_item.fields }),
  /** The lead's `plan` language with up to 300 changes, plus the import sugar (CreateItem status done, cancelled, in_progress). */
  plan: Schema.Struct({
    ...P,
    rationale: Schema.NonEmptyString.check(Schema.isMaxLength(2_000)),
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(300)),
    /** Wave O1: deduplicates retries (the explicit key wins over the tool-use id); stripped before the Project DO. */
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
    /**
     * Wave O1: an onboarding import. Top-level releases (and other plannable items) created with status todo or backlog
     * and no `owner` are owned by the importing person until a Start hands them to the lead.
     */
    import: Schema.optionalKey(Schema.Boolean),
  }),
  capture: Schema.Struct({
    ...P,
    kind: Schema.Literals(["idea", "feature", "bug", "question"]),
    title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
    body: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(40_000))),
    area: Schema.optionalKey(ToolRef),
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  close_item: Schema.Struct({
    ...P,
    ...closeItemFields,
    as: Schema.Literals(["done", "cancelled"]),
    note: Schema.NonEmptyString.check(Schema.isMaxLength(4_000)),
  }),
  comment: Schema.Struct({
    ...P,
    target: ToolRef,
    body: Schema.NonEmptyString.check(Schema.isMaxLength(40_000)),
    health: Schema.optionalKey(Health),
    /** Decision 0051: a decision on the item or goal it changed (what, why and the source); not with `health`. */
    decision: Schema.optionalKey(Schema.Boolean),
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  /** People record KPI samples with evidence optional (decision 0045). */
  record_kpi: Schema.Struct({
    ...P,
    ...GraphToolInputs.record_kpi.fields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  needs_me: Schema.Struct({ project: Schema.optionalKey(ToolRef) }),
  /** A plain question only (no effects, nothing gated); everything else is answered in the app. */
  answer: Schema.Struct({
    ...P,
    proposal: ToolRef,
    revision: Schema.Int,
    option: Schema.optionalKey(Slug),
    text: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(8_000))),
  }),
  /** Exactly one of `message` (a new message to the lead) and `replyTo` (the reply to an earlier one). */
  ask_lead: Schema.Union([
    Schema.Struct({ ...P, message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_000)) }),
    Schema.Struct({ ...P, replyTo: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)) }),
  ]),
  // ---- wave O1 (decision 0060): onboarding over MCP ----
  /** A Minga skill's exact `SKILL.md` (the same bytes as `skill://<name>/SKILL.md`), for clients without the extension. */
  guide: Schema.Struct({ name: SkillName }),
  /**
   * A new project with the web's defaults (the default charter, an empty brief, the default mind). The skill asks the
   * person first; Claude Code asks too (`anthropic/requiresUserInteraction`). `workspace`: an id or a name; required
   * when the person has several. Decision 0166 §4: `lead.name` is unique in the workspace (its handle is derived from
   * it); left out, a short name nobody in the workspace has is used.
   */
  create_project: Schema.Struct({
    name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
    lead: Schema.optionalKey(
      Schema.Struct({ name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(40)) }),
    ),
    repo: Schema.optionalKey(GitRemote),
    defaultBranch: Schema.optionalKey(BranchName),
    workspace: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100))),
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  /** Register a git remote (if new) and attach it to the project. Owners and admins only. */
  attach_repo: Schema.Struct({
    ...P,
    remote: GitRemote,
    defaultBranch: Schema.optionalKey(BranchName),
  }),
  /** The runners as the person sees them: of one project's workspace (`project`), one workspace, or all of theirs. */
  list_runners: Schema.Struct({
    project: Schema.optionalKey(ToolRef),
    workspace: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100))),
  }),
  /** G2: a link (`https:` only) on an item, as an artifact posted by the person. */
  attach: Schema.Struct({
    ...P,
    item: ToolRef,
    title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
    url: HttpsUrl,
    kind: Schema.optionalKey(Schema.Literals(["link", "document", "image", "report"])),
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  /** G8: undo an applied `plan` by the `changesetId` from its answer. */
  undo_plan: Schema.Struct({ ...P, changesetId: ChangesetId }),
  /**
   * Wave C (decision 0071): remember for the project, as the person ("Jannik via Claude Code"). Facts, learnings and
   * preferences and ordinary working rules apply under the person's authority, retaining their real author.
   * `area`: an area's ref or name (default: the project). Or `forget: "M-7"`.
   */
  remember: Schema.Struct({
    ...P,
    ...rememberFields,
    area: Schema.optionalKey(ToolRef),
    /** Decision 0146: `personal` is your own private note (PM-n), never in the project or anyone else's context. */
    scope: Schema.optionalKey(Schema.Literals(["project", "personal"])),
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  set_routing: Schema.Struct({
    ...P,
    policy: RoutingPolicy,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  set_workers: Schema.Struct({
    ...P,
    settings: WorkerSettings,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  request_work: Schema.Struct({
    ...P,
    destination: ToolRef,
    ...personWorkFields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  cancel_work: Schema.Struct({
    ...P,
    ...GraphToolInputs.cancel_work.fields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  link_work: Schema.Struct({
    ...P,
    ...GraphToolInputs.link_work.fields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  read_work: Schema.Struct({ ...P, ...GraphToolInputs.read_work.fields }),
  list_notifications: Schema.Struct({
    workspace: Schema.optionalKey(Schema.String),
    offset: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  }),
  mark_notifications_read: Schema.Struct({
    ids: Schema.Array(Schema.String.check(Schema.isPattern(/^ntf_[0-9a-hjkmnp-tv-z]{26}$/))).check(
      Schema.isMaxLength(100),
    ),
  }),
  set_subscription_account: Schema.Struct({
    workspace: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100))),
    subscription: Schema.String.check(Schema.isMinLength(1)),
    email: Schema.NullOr(
      Schema.Trim.check(Schema.isMaxLength(320), Schema.isPattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)),
    ),
  }),
  verify_work: Schema.Struct({
    ...P,
    ...GraphToolInputs.verify_work.fields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  /**
   * Decision 0142: a message as the person (through this client when it is one of their participants, "via @dot"): a
   * reply in a thread (`thread: "T-7"`; it lands in the chat or thread that wrote to you) or a new one to `to` (agents,
   * people). Messages to agents wake them.
   */
  message: Schema.Struct({
    ...P,
    ...GraphToolInputs.message.fields,
    idempotencyKey: Schema.optionalKey(IdempotencyKey),
  }),
  /**
   * Decision 0142: messages for you and this client that it has not picked up (agents' messages to your @dot or to this
   * coding session, replies to your ask_lead). Reading marks them read. `project` omitted: all your projects. `branch`:
   * this session's git branch, so the lead can address it by branch. `peek`: read without marking.
   */
  read_messages: Schema.Struct({
    project: Schema.optionalKey(ToolRef),
    branch: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
    peek: Schema.optionalKey(Schema.Boolean),
  }),
  /**
   * Decision 0142: let Minga's agents message this client as `@<handle>` (your ChatGPT dot as `@dot`), in every
   * project you are in. `off: true` disconnects it; `muted: true` keeps it but agents can't message it.
   */
  connect_participant: Schema.Struct({
    handle: Handle,
    workspace: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100))),
    muted: Schema.optionalKey(Schema.Boolean),
    off: Schema.optionalKey(Schema.Boolean),
  }),
} as const
export type PersonToolName = keyof typeof PersonToolInputs
export const PersonToolNames = Object.keys(PersonToolInputs) as ReadonlyArray<PersonToolName>

/** The external MCP server's `serverInfo.name` (wave O1): distinct from the run proxy's `minga`, so runners can tell them apart. */
export const MINGA_CLOUD_SERVER_NAME = "minga-cloud"

/**
 * Decision 0162: tools only Minga's external MCP server serves together. A runner recognises the person's own Minga
 * connector inside a CLI's connector bundle (Codex Apps, whose app name is the person's choice and whose URL is not
 * reported) by these names, and switches it off for runs. Keep them person tools that stay served.
 */
export const MINGA_CLOUD_SIGNATURE_TOOLS = [
  "list_projects",
  "read_project",
  "ask_lead",
] as const satisfies ReadonlyArray<PersonToolName>

/** MCP tool annotations (spec `ToolAnnotations`). Hints only: clients never trust them for safety. */
export interface PersonToolAnnotations {
  readonly title: string
  readonly readOnlyHint: boolean
  readonly destructiveHint?: boolean
  readonly idempotentHint?: boolean
  readonly openWorldHint: false
}

export interface PersonToolMeta {
  /** The tool's display title (`Tool.title`, also `annotations.title`). */
  readonly title: string
  readonly annotations: PersonToolAnnotations
  /** `Tool._meta`: Claude Code's `anthropic/requiresUserInteraction` on the two tools that change projects themselves. */
  readonly _meta?: { readonly "anthropic/requiresUserInteraction": true }
}

const READ = (title: string): PersonToolMeta => ({
  title,
  annotations: { title, readOnlyHint: true, openWorldHint: false },
})
const WRITE = (
  title: string,
  destructiveHint: boolean,
  idempotentHint: boolean,
  confirm = false,
): PersonToolMeta => ({
  title,
  annotations: { title, readOnlyHint: false, destructiveHint, idempotentHint, openWorldHint: false },
  ...(confirm ? { _meta: { "anthropic/requiresUserInteraction": true } as const } : {}),
})

/**
 * Wave O1 (wave spec §3.3): title, annotations and `_meta` of every person tool, read by the cloud's `tools/list` and
 * the tests. `plan` is destructive (it can cancel and archive) and not idempotent (dedupe is by seed);
 * `create_project` is not idempotent either (only a call with the same key or tool-use id replays).
 */
export const PERSON_TOOL_META: Readonly<Record<PersonToolName, PersonToolMeta>> = {
  request_work: WRITE("Delegate or retarget work", false, false),
  list_notifications: READ("Read my notification inbox"),
  mark_notifications_read: WRITE("Mark notifications read", false, false),
  cancel_work: WRITE("Stop delegated work", false, false),
  link_work: WRITE("Link delegated work", false, false),
  verify_work: WRITE("Verify returned work", false, false),
  read_work: READ("Read delegated work"),
  set_subscription_account: WRITE("Share subscription usage across runners", false, true),
  set_routing: WRITE("Set project work routing", true, false),
  set_workers: WRITE("Set project workers and budgets", true, false),
  list_projects: READ("List my Minga projects"),
  read_project: READ("Read a Minga project"),
  read_item: READ("Read a Minga item"),
  plan: WRITE("Change a Minga project's plan", true, false),
  capture: WRITE("Capture into Triage", false, false),
  close_item: WRITE("Close an item", true, false),
  comment: WRITE("Comment", false, false),
  record_kpi: WRITE("Record a KPI value", false, false),
  needs_me: READ("What waits on me"),
  answer: WRITE("Answer a question", false, false),
  ask_lead: WRITE("Message the lead", false, false),
  guide: READ("Read a Minga skill"),
  create_project: WRITE("Create a Minga project", false, false, true),
  attach_repo: WRITE("Attach a repo to a project", false, true, true),
  list_runners: READ("List my runners"),
  attach: WRITE("Attach a link to an item", false, false),
  undo_plan: WRITE("Undo a plan change", true, false),
  remember: WRITE("Remember for the project", false, false),
  message: WRITE("Message an agent or a thread", false, false),
  read_messages: WRITE("Read messages for me", false, true),
  connect_participant: WRITE("Let Minga's agents message this client", false, true),
}

// #region person-tool data (the `data` of each new tool's answer)

/** Problems that keep the lead or the work from moving (core `SetupProblem` tags, wave B2; moved to core in O1). */
export const SETUP_PROBLEM_TAGS = [
  "no_key",
  "key_refused",
  "cli_signed_out",
  "no_runner",
  "runner_offline",
  "owner_left",
  "no_cli",
  "no_credit", // decision 0135: the provider account behind the cloud lead's key is out of credit
] as const
export const SetupProblemTag = Schema.Literals(SETUP_PROBLEM_TAGS)
export type SetupProblemTag = typeof SetupProblemTag.Type

/**
 * Wave O1 (wave spec §3.4): can the project's lead think, and can work run? Returned by `read_project`,
 * `create_project` and `attach_repo`. `fix` is "" when nothing is wrong (a missing repo is named there too); its next
 * step depends on who asks.
 */
export const ProjectSetup = Schema.Struct({
  /** The lead's owner's runners in this workspace. */
  runner: Schema.Literals(["online", "offline", "none"]),
  /** "you" when the caller owns the lead, else the owner's masked display name ("another member" when unknown). */
  leadOwner: Schema.String,
  /** Does an online runner of the lead's owner serve the project's repo? null: the project has no repo. */
  servesRepo: Schema.NullOr(Schema.Boolean),
  repo: Schema.Literals(["attached", "none"]),
  leadCanWork: Schema.Boolean,
  problems: Schema.Array(SetupProblemTag),
  /** Live runs waiting with no_runner_online / runner_offline. */
  waitingForRunner: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  fix: Schema.String,
})
export type ProjectSetup = typeof ProjectSetup.Type

/** `guide`: the skill's exact bytes plus what never goes into them. */
export const GuideData = Schema.Struct({
  name: SkillName,
  uri: Schema.String,
  digest: SkillDigest,
  /** The exact `SKILL.md` (frontmatter and body), byte-identical to `resources/read`. */
  text: Schema.String,
  /** Today as YYYY-MM-DD (UTC). */
  today: Schema.String,
  origin: Schema.String,
})
export type GuideData = typeof GuideData.Type

/** `create_project`. `lead.mind` is a readable line: "runner mac-mini (claude-default)", "the workspace's Anthropic key". */
export const CreateProjectData = Schema.Struct({
  project: Schema.Struct({ id: ProjectId, slug: Slug, name: Schema.String, url: Schema.String }),
  workspace: Schema.Struct({ id: WorkspaceId, name: Schema.String }),
  repo: Schema.NullOr(Schema.Struct({ remote: Schema.String, defaultBranch: Schema.String })),
  lead: Schema.Struct({ name: Schema.String, handle: Handle, mind: Schema.String }),
  /** true: a replay (same key or tool-use id), or dedupe by remote or by a recent same name; nothing was created. */
  existing: Schema.Boolean,
  setup: ProjectSetup,
  /** One line: what to do next ("Read the onboard-project skill, step 2."). */
  next: Schema.String,
})
export type CreateProjectData = typeof CreateProjectData.Type

/** `attach_repo`. `attached: false`: it was attached already; nothing was written. */
export const AttachRepoData = Schema.Struct({
  project: Schema.Struct({ id: ProjectId, slug: Slug }),
  repo: Schema.Struct({ id: RepoId, remote: Schema.String, defaultBranch: Schema.String }),
  attached: Schema.Boolean,
  setup: ProjectSetup,
})
export type AttachRepoData = typeof AttachRepoData.Type

/** `plan` (wave O1): the project's URL and what the committed changes did. */
export const PlanCounts = Schema.Struct({
  /** CreateItem per item type ("release": 6, "feature": 23). */
  created: Schema.Record(Schema.String, Schema.Int),
  closed: Schema.Struct({ done: Schema.Int, cancelled: Schema.Int }),
  inProgress: Schema.Int,
  areas: Schema.Int,
  /** A ReviseBrief applied. */
  brief: Schema.Boolean,
})
export type PlanCounts = typeof PlanCounts.Type
export const PlanData = Schema.Struct({ url: Schema.String, counts: PlanCounts })
export type PlanData = typeof PlanData.Type

/** `attach` (G2). */
export const AttachData = Schema.Struct({ artifactId: ArtifactId, item: Schema.String })
export type AttachData = typeof AttachData.Type

/** `undo_plan` (G8): `undone` is the target changeset, `changesetId` the undo's own. */
export const UndoPlanData = Schema.Struct({ undone: ChangesetId, changesetId: ChangesetId })
export type UndoPlanData = typeof UndoPlanData.Type

// #endregion person-tool data
// #endregion person-tools

// #region repo-tool
/** Served by the runner's local MCP server "repo" (W9); never by the cloud. Always a draft PR this wave. */
export const OpenPullRequestInput = Schema.Struct({
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  body: Schema.String.check(Schema.isMaxLength(20_000)),
  /** Decision 0117: the PR's base branch when it is not the task's base (default: the task's base branch). */
  base: Schema.optionalKey(BranchName),
})
export const OpenPullRequestOutput = Schema.Struct({
  url: Schema.String,
  number: PosInt,
  branch: Schema.String,
  headSha: Schema.String,
  artifactId: Schema.NullOr(ArtifactId),
  /** The files committed by this call, also listed in the PR body (B13). */
  files: Schema.Array(Schema.String),
})
// #endregion repo-tool

// #region prompts
/**
 * Wave O1 (decision 0060): the slash prompts wrap the skills (`/minga:onboard`, `/minga:hand_off`). Prompt arguments
 * split on whitespace in Claude Code, so the web passes the project's id. The web's Codex "Copy the onboarding prompt"
 * uses `onboardPrompt` too.
 */
export const ONBOARD_PROMPT_NAME = "onboard"
export const HAND_OFF_PROMPT_NAME = "hand_off"
/** The old name of `onboard`, served as an alias for one release (remove in the next release that touches prompts). */
export const IMPORT_PROMPT_NAME = "import_repo_state"

const projectLine = (project: string | null): string =>
  project === null || project.trim() === ""
    ? "find it by this repo's git remote"
    : `the Minga project ${JSON.stringify(project.trim())}`

/** Two preamble lines (the skill, the project, today) and the skill's body with `{{origin}}` filled in. */
const skillPrompt = (skill: SkillName, project: string | null, today: string, origin: string): string =>
  `Follow the Minga skill ${skill} below. Project: ${projectLine(project)}.\nToday: ${today}.\n\n${skillBody(skill, origin)}`

/** `/minga:onboard [project]`: the onboard-project skill with the project (or "find it by this repo") and today. */
export const onboardPrompt = (project: string | null, today: string, origin: string): string =>
  skillPrompt("onboard-project", project, today, origin)

/** `/minga:hand_off [project]`: the hand-off skill with the project and today. */
export const handOffPrompt = (project: string | null, today: string, origin: string): string =>
  skillPrompt("hand-off", project, today, origin)

/**
 * @deprecated Wave O1: use `onboardPrompt(project, today, origin)`. Kept so callers compile until they move; it is the
 * onboarding prompt against production.
 */
export const importPrompt = (project: string, date: string): string =>
  onboardPrompt(project, date, DEFAULT_ORIGIN)
// #endregion prompts
